import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  TextField,
  Button,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  Divider,
  makeStyles,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { moment } from "moment";
import { useTheme } from "@material-ui/core/styles";
import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useDispatch, useSelector } from "react-redux";
import {
  actions,
  getDataFromDashboard,
  updateLoginBegin,
} from "../../redux/user";
import ChangePwd from "../../Components/ChangePwd";
import DatePicker from "../../Components/DatePicker";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const useStyles = makeStyles((theme) => ({
  inputMarginDense: {
    textAlign: "center",
  },
  menuPaper: {
    marginTop: theme.spacing(1),
    top: "48px !important",
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    "& .MuiMenuItem-root:hover": {
      backgroundColor: "#fff5f0",
    },
  },
  card: {
    marginTop: theme.spacing(2),
  },
  drawerPaper: {
    width: 200,
  },
  drawerHeader: {
    display: "flex",
    padding: theme.spacing(1),
    backgroundColor: "#F8F8F8",
    color: "#000000",
    fontSize: "1.1rem",
  },
  backIcon: {
    color: "#FF5800",
    display: "flex",
    justifyContent: "flex-start",
  },
  menuHeading: {
    fontSize: "1.1rem",
    fontWeight: "500",
    fontStyle: "italic",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: "5px",
    fontFamily: "Roboto sans-serif",
  },
  listItem: {
    "&:hover": {
      backgroundColor: "#fff5f0",
    },
  },
}));

// ============================================================
// ✅ iOS-SAFE DATE HELPERS — defined OUTSIDE component
// iOS Safari cannot parse "2024-03-07" with new Date()
// Fix: split string into parts → new Date(year, month-1, day)
// ============================================================

const parseDateSafe = (date) => {
  if (date instanceof Date) {
    console.log("[parseDateSafe] Already Date object:", date.toString());
    return date;
  }
  if (typeof date === "string" && date.includes("-")) {
    const parts = date.split("-").map(Number);
    const result = new Date(parts[0], parts[1] - 1, parts[2]);
    console.log(
      "[parseDateSafe] String input:", date,
      "→ Parsed:", result.toString(),
      "| getDate():", result.getDate(),
      "| isValid:", !isNaN(result.getTime())
    );
    return result;
  }
  const fallback = new Date(date);
  console.log("[parseDateSafe] Fallback:", date, "→", fallback.toString(), "| isValid:", !isNaN(fallback.getTime()));
  return fallback;
};

const addOneDay = (dateStr) => {
  const original = parseDateSafe(dateStr);
  const d = new Date(original); // clone date
  d.setDate(d.getDate() + 1);
  return d;
};

const formatDate = (date, isFromDate = true) => {
  const d = date instanceof Date ? date : parseDateSafe(date);
  const dayStr = `${d.getDate()}`.padStart(2, "0");
  const monthStr = `${d.getMonth() + 1}`.padStart(2, "0");
  const yearStr = d.getFullYear();
  const time = isFromDate ? "06:00:00 AM" : "05:59:59 AM";
  const result = `${monthStr}-${dayStr}-${yearStr} ${time}`;
  console.log(
    "[formatDate] Input:", typeof date === "object" ? date.toString() : date,
    "| isFromDate:", isFromDate,
    "→ Output:", result
  );
  return result;
};

// ============================================================

const Header = ({ data }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Log todayISO on every render to confirm it's valid
  const todayISO = new Date().toISOString().split("T")[0];
  console.log("[Header] todayISO on render:", todayISO);

  const [fromDate, setFromDate] = useState(todayISO);
  const [toDate, setToDate] = useState(todayISO);
  const [dateError, setDateError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [open, setOpen] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [selectedBranchCode, setSelectedBranchCode] = useState(null);

  const theme = useTheme();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.loggedInUserId);
  const branchCode = useSelector((state) => state.user.branchCode);

  const handleChangePassword = (
    password,
    confirmPassword,
    setPasswordError,
    setConfirmPasswordError,
    setShowAlert,
  ) => {
    if (!password || !confirmPassword) {
      setPasswordError("Password fields cannot be empty");
      setConfirmPasswordError("Password fields cannot be empty");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      setConfirmPasswordError("Passwords do not match");
      return;
    }
    if (!userId) {
      setPasswordError("User ID is missing");
      return;
    }
    const payload = { userId, Pwd: password };
    dispatch(actions.updatePasswordBegin(payload));
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
    setOpen(false);
  };

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const validateDates = () => {
    setDateError("");
    if (!fromDate || !toDate) {
      setDateError("Please select both dates");
      return false;
    }
    const startDate = parseDateSafe(fromDate); // ✅ iOS safe
    const endDate = parseDateSafe(toDate);     // ✅ iOS safe
    console.log("[validateDates] startDate:", startDate.toString(), "| endDate:", endDate.toString());
    if (startDate > endDate) {
      setDateError("To Date must be after From Date");
      return false;
    }
    return true;
  };

  const handleRefresh = () => {
    console.log("[handleRefresh] fromDate:", fromDate, "| toDate:", toDate, "| userId:", userId);
    const sortedBranches = compInfo
      .slice()
      .sort((a, b) => a.branchCode - b.branchCode);
    const branchCodeToUse =
      selectedBranchCode || (sortedBranches[0] && sortedBranches[0].branchCode);
    const formattedFromDate = formatDate(fromDate, true);
    const formattedToDate = formatDate(addOneDay(toDate), false); // ✅ iOS safe
    console.log("[handleRefresh] Sending to API → fromDate:", formattedFromDate, "toDate:", formattedToDate);
    dispatch(
      getDataFromDashboard(
        formattedFromDate,
        formattedToDate,
        userId,
        branchCodeToUse,
      ),
    );
    console.log("Refresh clicked");
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      const currentDate = new Date().toISOString().split("T")[0];
      console.log("[visibilityChange] Resetting to:", currentDate);
      setFromDate(currentDate);
      setToDate(currentDate);
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = setInterval(() => {
      if (fromDate && toDate) {
        handleRefresh();
      }
      console.log("[Interval] Triggered - 5 min");
    }, 300000);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [fromDate, toDate]);

  useEffect(() => {
    console.log("[useEffect dates/mobile] isMobile:", isMobile, "| fromDate:", fromDate, "| toDate:", toDate, "| userId:", userId, "| branchCode:", branchCode);
    if (isMobile === true) {
      if (validateDates()) {
        const formattedFromDate = formatDate(fromDate, true);
        const formattedToDate = formatDate(addOneDay(toDate), false); // ✅ iOS safe
        console.log("[useEffect MOBILE] API params → fromDate:", formattedFromDate, "| toDate:", formattedToDate);
        dispatch(
          getDataFromDashboard(
            formattedFromDate,
            formattedToDate,
            userId,
            selectedBranchCode || branchCode,
          ),
        );
        console.log("Mobile view - data fetched");
      }
    } else {
      if (validateDates()) {
        const formattedFromDate = formatDate(fromDate, true);
        const formattedToDate = formatDate(addOneDay(toDate), false); // ✅ iOS safe
        console.log("[useEffect DESKTOP] API params → fromDate:", formattedFromDate, "| toDate:", formattedToDate);
        dispatch(
          getDataFromDashboard(
            formattedFromDate,
            formattedToDate,
            userId,
            selectedBranchCode || branchCode,
          ),
        );
        console.log("Desktop view - data fetched");
      }
    }
  }, [isMobile, fromDate, toDate, dispatch]);

  const handleBranchClick = (branchCode, branchName) => {
    console.log("[handleBranchClick] branch:", branchCode, "| fromDate:", fromDate, "| toDate:", toDate);
    const formattedFromDate = formatDate(fromDate, true);
    const formattedToDate = formatDate(addOneDay(toDate), false); // ✅ iOS safe
    dispatch(
      getDataFromDashboard(
        formattedFromDate,
        formattedToDate,
        userId,
        branchCode,
      ),
    );
    setSelectedBranchCode(branchCode);
    setSelectedBranchName(branchName);
    setMobileOpen(false);
    console.log("Branch clicked:", branchCode, branchName);
  };

  const compInfo = Array.isArray(data?.companyInfo) ? data.companyInfo : [];

  useEffect(() => {
    if (!initialDataLoaded && compInfo.length > 0) {
      console.log("[initialDataLoaded useEffect] fromDate:", fromDate, "| toDate:", toDate);
      const sortedBranches = compInfo
        .slice()
        .sort((a, b) => a.branchCode - b.branchCode);
      const initialBranch = sortedBranches[0];
      if (initialBranch) {
        setSelectedBranchName(initialBranch.branchName);
        setSelectedBranchCode(initialBranch.branchCode);
        const formattedFromDate = formatDate(fromDate, true);
        const formattedToDate = formatDate(addOneDay(toDate), false); // ✅ iOS safe
        console.log("[initialDataLoaded] API params → fromDate:", formattedFromDate, "| toDate:", formattedToDate, "| branch:", initialBranch.branchCode);
        dispatch(
          getDataFromDashboard(
            formattedFromDate,
            formattedToDate,
            userId,
            initialBranch.branchCode,
          ),
        );
        console.log("Initial data fetched for branch:", initialBranch.branchCode);
      }
      setInitialDataLoaded(true);
    }
  }, [initialDataLoaded, compInfo, fromDate, toDate, dispatch, userId]);

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        style={{
          backgroundColor: "#FF5800",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              style={{
                color: "#ffffff",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box style={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              style={{
                color: "#ffffff",
                textAlign: isMobile ? "center" : "left",
                fontSize: "1.1rem",
                fontWeight: 500,
              }}
            >
              {compInfo?.length > 0 && compInfo[0].compName}
            </Typography>
            <Typography
              variant="subtitle1"
              component="div"
              style={{
                color: "#ffffff",
                textAlign: isMobile ? "center" : "left",
                fontSize: "0.9rem",
                fontWeight: 400,
              }}
            >
              {selectedBranchName}
            </Typography>
          </Box>

          {!isMobile && (
            <Box
              style={{
                display: "flex",
                gap: 24,
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                alignItems: "center",
              }}
            >
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography style={{ color: "#ffffff", minWidth: "45px" }}>
                  From:
                </Typography>
                <DatePicker
                  color="#ffffff"
                  setValue={setFromDate}
                  value={fromDate}
                />
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography style={{ color: "#ffffff", minWidth: "45px" }}>
                  To:
                </Typography>
                <DatePicker
                  color="#ffffff"
                  setValue={setToDate}
                  value={toDate}
                />
              </Box>
              {dateError && (
                <Typography style={{ color: "#ffffff", fontSize: "0.75rem" }}>
                  {dateError}
                </Typography>
              )}
            </Box>
          )}

          <IconButton
            size="large"
            onClick={handleRefresh}
            style={{
              color: "#ffffff",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            <RefreshIcon />
          </IconButton>

          <IconButton
            size="large"
            onClick={handleMenu}
            style={{
              color: "#ffffff",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            classes={{ paper: classes.menuPaper }}
          >
            <MenuItem onClick={() => setOpen(true)}>Change Password</MenuItem>
            <ChangePwd
              open={open}
              handleClose={() => setOpen(false)}
              handleChangePassword={handleChangePassword}
              handleCloseMenu={handleClose}
            />
            <Divider style={{ margin: "5px 0" }} />
            <MenuItem
              component={RouterLink}
              to="/"
              onClick={() => {
                const payload = { userId: userId, isLogin: false };
                dispatch(updateLoginBegin(payload));
                dispatch(actions.logOut());
                handleClose();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        classes={{ paper: classes.drawerPaper }}
      >
        <IconButton className={classes.backIcon} onClick={handleDrawerToggle}>
          <ArrowBackIcon />
        </IconButton>
        <Box className={classes.drawerHeader}>
          <Typography variant="h6">
            {compInfo?.length > 0 && compInfo[0].compName}
          </Typography>
        </Box>
        <Divider />
        <Box>
          <Typography
            className={classes.menuHeading}
            variant="subtitle1"
            gutterBottom
          >
            Branches
          </Typography>
          <Divider />
          <List style={{ marginLeft: theme.spacing(1.5), padding: "0" }}>
            {[
              ...(compInfo?.length > 1 ? ["All"] : []),
              ...compInfo
                .slice()
                ?.sort((a, b) => a.branchCode - b.branchCode)
                .map((item) => item.branchName),
            ].map((menuItem, index, array) => (
              <React.Fragment key={index}>
                <ListItem
                  className={classes.listItem}
                  button
                  onClick={() =>
                    handleBranchClick(
                      menuItem === "All"
                        ? 0
                        : compInfo.find((branch) => branch.branchName === menuItem)?.branchCode,
                      menuItem === "All"
                        ? "All Branches"
                        : compInfo.find((branch) => branch.branchName === menuItem)?.branchName,
                    )
                  }
                >
                  <Typography style={{ fontSize: "12px", textWrap: "nowrap" }}>
                    {menuItem}
                  </Typography>
                </ListItem>
                {index < array.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>

      {isMobile && (
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            textAlign: "center",
            justifyContent: "center",
            gap: 2,
            mt: 3,
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography style={{ color: "#333333", minWidth: "45px" }}>
              From:
            </Typography>
            <DatePicker setValue={setFromDate} value={fromDate} />
          </Box>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              marginLeft: 4,
            }}
          >
            <Typography sx={{ color: "#333333", minWidth: "45px" }}>
              To:
            </Typography>
            <DatePicker setValue={setToDate} value={toDate} />
          </Box>
          {dateError && (
            <Typography style={{ color: "#d32f2f", fontSize: "0.75rem" }}>
              {dateError}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

export default Header;